import {createApp, h} from 'vue';
import {createPinia} from 'pinia/dist/pinia';
import VueExcelXlsx from "vue-excel-xlsx";
import router from './router';
import App from './App.vue';
import PrimeVue from 'primevue/config';
import BadgeDirective from 'primevue/badgedirective';
import ConfirmationService from 'primevue/confirmationservice';
import Ripple from 'primevue/ripple';
import StyleClass from 'primevue/styleclass';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';
import CodeHighlight from './AppCodeHighlight';
import BlockViewer from './BlockViewer';
import * as PrimeVueConfig from './primevue';
import Sso from "@/pages/Sso";
import {useUserStore} from "@/store/userStore";
import IdleVue from 'idle-vue-3';
import moment from 'moment';
import {createI18n} from 'vue-i18n/dist/vue-i18n.cjs.js';
import axios from 'axios';
import {useEndpointStore} from "@/store/endpointStore";
import {useApplicationStore} from "@/store/applicationStore";
import {useAclStore} from "@/store/aclStore";
import VueParticles from "vue-particles";
import Login from "@/pages/Login";
import LandingPage from "@/pages/LandingPage";
import mitt from 'mitt';
import SsoService from "@/service/SsoService";
import jwt_decode from "jwt-decode";
import LoginService from "@/service/LoginService";
import 'primevue/resources/primevue.min.css';
import 'primeflex/primeflex.css';
import 'primeicons/primeicons.css'
import 'prismjs/themes/prism-coy.css';
import SsoCofanet from "./pages/Sso-cofanet.vue";

const pinia = createPinia();
const emitter = mitt();
const userLanguage = navigator.language.substring(0, 2);
const i18n = createI18n({
    locale: userLanguage,
    fallbackLocale: 'en',
    messages: {
        en: require('./lang/en.json'),
        it: require('./lang/it.json'),
    },
    idleTime: 600000,
    startAtIdle: false
});

const app = createApp({
    computed: {
        ViewComponent() {
            switch (this.$route.path) {
                case '/login':
                    return Login;
                case '/sso':
                    return Sso;
                case '/sso-cofanet':
                    return SsoCofanet;
                case '/landing_page':
                    return LandingPage;
                default:
                    return App;
            }
        }
    },
    render() {
        return h(this.ViewComponent)
    }
});
app.use(VueParticles)
app.use(PrimeVue, {ripple: true});
app.use(ConfirmationService);
app.use(ToastService);
app.use(router);
app.use(pinia);
app.use(VueExcelXlsx);
app.use(IdleVue, {eventEmitter: emitter, idleTime: 600000, startAtIdle: false});
app.use(i18n);

app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);
app.directive('code', CodeHighlight);
app.directive('badge', BadgeDirective);
app.directive('styleclass', StyleClass);
app.component('BlockViewer', BlockViewer);

app.config.globalProperties.$toolips = {
    standard(text) {
        return {
            value: text,
            pt: {
                arrow: {
                    style: {
                        borderBottomColor: 'var(--primary-color)'
                    }
                },
                text: 'bg-primary font-medium'
            }
        }
    }
}

app.config.globalProperties.$validator = {
    normalize(value) {
        return value != null && typeof value === 'string' ? value.replace(/\s/g, "") : ""
    },
    validatePartitaiva(pi) {
        if (pi === '00000000000') {
            return false
        } else {
            pi = this.normalize(pi);
            if (pi.length === 13 && pi.startsWith("IT")) {
                pi = pi.substr(2);
            }
            if (pi.length === 0)
                return false;
            else if (pi.length !== 11)
                return false;
            if (!/^[0-9]{11}$/.test(pi))
                return false;
            let s = 0;
            for (let i = 0; i < 11; i++) {
                let n = pi.charCodeAt(i) - "0".charCodeAt(0);
                if ((i & 1) === 1) {
                    n *= 2;
                    if (n > 9)
                        n -= 9;
                }
                s += n;
            }
            return s % 10 === 0;
        }
    },
    validateCodicefiscale(cf) {
        cf = this.normalize(cf).toUpperCase();
        if (cf.length === 0)
            return false;
        else if (cf.length === 16)
            return this.PRIVATE_validate_regular(cf);
        else if (cf.length === 11)
            return this.PRIVATE_validate_temporary(cf);
        else
            return false;
    },
    PRIVATE_validate_regular(cf) {
        if (!/^[0-9A-Z]{16}$/.test(cf))
            return false;
        let s = 0;
        const even_map = "BAFHJNPRTVCESULDGIMOQKWZYX";
        for (let i = 0; i < 15; i++) {
            const c = cf[i];
            let n = 0;
            if ("0" <= c && c <= "9")
                n = c.charCodeAt(0) - "0".charCodeAt(0);
            else
                n = c.charCodeAt(0) - "A".charCodeAt(0);
            if ((i & 1) === 0)
                n = even_map.charCodeAt(n) - "A".charCodeAt(0);
            s += n;
        }
        return s % 26 + "A".charCodeAt(0) === cf.charCodeAt(15);
    },
    PRIVATE_validate_temporary(cf) {
        if (!/^[0-9]{11}$/.test(cf))
            return false;
        let s = 0;
        for (let i = 0; i < 11; i++) {
            let n = cf.charCodeAt(i) - "0".charCodeAt(0);
            if ((i & 1) === 1) {
                n *= 2;
                if (n > 9)
                    n -= 9;
            }
            s += n;
        }
        return s % 10 === 0;

    }
}

app.config.globalProperties.$endpoints = {
    createRoles(allRoles) {
        let roles = [];
        const applicationStore = useApplicationStore()
        const latitudiniRoles = allRoles.filter(role => role.includes("assicurati-credito:latitudini")).map(role => role.split(":")[1]);
        const cofaceWebAssicuratiRoles = allRoles.filter(role => role.includes("cofacewebassicurati")).map(role => role.split(":")[1]);
        latitudiniRoles.forEach(role => roles.push(role));
        cofaceWebAssicuratiRoles.forEach(role => roles.push(role));
        if (latitudiniRoles.length === 0 && cofaceWebAssicuratiRoles.length === 1) {
            applicationStore.setApplicationCode("cofacewebassicurati")
        }
        return {
            roles: roles,
            latitudiniEnabled: latitudiniRoles.length > 0,
            cofaceWebAssicuratiEnabled: cofaceWebAssicuratiRoles.length > 0
        }
    },
    redirectToSsoHomepage() {
        this.ssoService.getSsoHomepage().then(url => {
            window.location.replace(url);
        })
    },
    userInfo(token, router, vueInstance) {
        const ssoService = new SsoService()
        const userStore = useUserStore()
        const endpointStore = useEndpointStore()
        const objRoles = this.createRoles(jwt_decode(token.jwt).groups)
        const loginService = new LoginService();
        loginService.getCofaceWebAssicuratiEndpoint().then(endpoint => {
            endpointStore.setCofaceWebAssicuratiEndpoint(endpoint)
        });
        ssoService.userInfo().then(userInfo => {
            if (userInfo != null) {
                let user = userInfo
                user.token = token.jwt
                user.refresh = token.refresh
                const mainProfile = userInfo.profiles.find(profile => profile.main === true)
                user.profile = mainProfile != null ? mainProfile : userInfo.profiles[0]
                user.latitudiniEnabled = objRoles.latitudiniEnabled
                user.cofaceWebAssicuratiEnabled = objRoles.cofaceWebAssicuratiEnabled
                let count = 0
                if (user.latitudiniEnabled) count++
                if (user.cofaceWebAssicuratiEnabled) count++
                if (count > 1) router.push('/landing_page');
                if (count === 1 && user.latitudiniEnabled) router.push('/');
                if (count === 1 && user.cofaceWebAssicuratiEnabled) this.goToCofaceWebAssicurati();
                userStore.login(user, vueInstance);
            } else {
                this.redirectToSsoHomepage();
            }
        }).catch(() => {
            vueInstance.$toast.add({
                severity: "error",
                summary: vueInstance.$t("toasts.somethingWentWrong.summary"),
                detail: vueInstance.$t("toasts.somethingWentWrong.detail"),
                life: 4000
            });
        });
    },
    refreshEndpoints() {
        const endpointStore = useEndpointStore()
        let endpoint = location.protocol + '//' + location.hostname;
        endpointStore.setEndpoint(endpoint + "/graphql");
        endpointStore.setExportEndpoint(endpoint + "/export");
        endpointStore.setSsoEndpoint(endpoint + "/sso");
        endpointStore.setIamBackendEndpoint(endpoint + "/iam-backend");
        if (location.hostname.search("localhost") !== -1) {
            endpointStore.setEndpoint('https://ns-mar-italy-assicurati-credito-graphql.tkgmardev.coface.dns');
            endpointStore.setExportEndpoint('https://ns-mar-italy-export.tkgmardev.coface.dns');
            endpointStore.setSsoEndpoint("https://ns-mar-italy-sso.tkgmardev.coface.dns");
            endpointStore.setIamBackendEndpoint("https://ns-mar-italy-iam-backend.tkgmardev.coface.dns");
        }
    },
    goToCofaceWebAssicurati() {
        const ssoService = new SsoService()
        const endpointStore = useEndpointStore()
        const userStore = useUserStore()
        const action = {application: 'cofacewebassicurati', refresh: userStore.user.refresh}
        ssoService.sendAction(action).then(actionId => {
            const posAction = actionId.indexOf('actionId');
            const onlyActionId = actionId.substring(posAction)
            window.open(endpointStore.cofaceWebAssicuratiEndpoint + '?' + onlyActionId, '_self');
        })
    },
}

app.config.globalProperties.$xls = {

    createColumns(obj, lang, vueInstance) {
        const fields = Object.getOwnPropertyNames(obj);
        let columnsList = []
        fields.forEach(element => {
            columnsList.push({
                label: vueInstance.$t(lang + element),
                field: element
            })
        })
        return columnsList
    }
}

app.config.globalProperties.$methods = {
    deepClone(object) {
        if (object != null) {
            return JSON.parse(JSON.stringify(object))
        }
    },
    formatDate(value, format) {
        if (value && format) {
            return moment(String(value)).format(format)
        }
    },
    isEmpty(object) {
        return (object == null || object === "" || object.length === 0 || typeof object === 'undefined')
    },
    isEditable(elementId) {
        const aclStore = useAclStore()
        let editable = true
        aclStore.aclRules.filter(aclRule => aclRule.elementId === elementId).forEach(aclRule => {
            if (aclRule.editable !== true) editable = false;
        })
        return editable
    },
    isVisible(elementId) {
        const aclStore = useAclStore()
        return (aclStore.aclRules.find(a => a.elementId === elementId) ? aclStore.aclRules.find(a => a.elementId === elementId).visible : true)
    },
}

PrimeVueConfig.init(app);
app.mount('#app');

export function getUser() {
    if (app != null && useUserStore != null) {
        const userStore = useUserStore()
        return userStore.user;
    }
}

export function setUser(user) {
    if (app != null && useUserStore != null) {
        const userStore = useUserStore()
        return userStore.setUser(user);
    }
}

export function getApplicationCode() {
    if (app != null && useApplicationStore != null) {
        const applicationStore = useApplicationStore()
        return applicationStore.applicationCode;
    }
}

export function getAxios() {
    const endpointStore = useEndpointStore()
    axios.defaults.baseURL = endpointStore.endpoint;
    axios.defaults.headers.common['x-application-code'] = getApplicationCode()
    if (getUser() != null && getUser().token != null) {
        axios.defaults.headers.common.authorization = `Bearer ${getUser() != null ? getUser().token : null}`;
    }
    return axios;
}

export function getExportAxios() {
    const endpointStore = useEndpointStore()
    axios.defaults.baseURL = endpointStore.exportEndpoint;
    axios.defaults.headers.common = {};
    return axios;
}

export function getSsoAxios() {
    const endpointStore = useEndpointStore()
    axios.defaults.baseURL = endpointStore.ssoEndpoint;
    axios.defaults.headers.common['x-application-code'] = getApplicationCode()
    if (getUser() != null && getUser().token != null) {
        axios.defaults.headers.common.authorization = `Bearer ${getUser() != null ? getUser().token : null}`;
    }
    return axios;
}

export function getIamBackendAxios() {
    const endpointStore = useEndpointStore()
    axios.defaults.baseURL = endpointStore.iamBackendEndpoint;
    axios.defaults.headers.common['x-application-code'] = getApplicationCode()
    if (getUser() != null && getUser().token != null) {
        axios.defaults.headers.common.authorization = `Bearer ${getUser() != null ? getUser().token : null}`;
    }
    return axios;
}

export function getAssicuratiCreditoAxios() {
    const endpointStore = useEndpointStore()
    axios.defaults.baseURL = endpointStore.endpoint;
    return axios;
}

export function executeQuery(query, variables) {
    return getAxios().post('graphql', {
        query: query,
        variables: variables
    }).then(result => {
        if (result.data.errors != null && result.data.errors.length > 0) {
            console.log(result.data.errors);
            let errorKey = null;
            result.data.errors.forEach(e => {
                if (e.exception != null && e.exception.errorKey != null) {
                    errorKey = e.exception.errorKey;
                }
            })
            throw {
                request: {status: 200},
                response: {data: {errorKey: errorKey != null ? errorKey : 'graphql'}}
            };
        }
        if (result.data.dataPresent) {
            return result.data.data[Object.keys(result.data.data)[0]];
        }
    }).catch(e => {
        console.log("Error calling '" + query + "' graphql query")
        if (e.request.status === 403) {
            setUser(null);
        }
        throw e;
    });
}

export function formatDate(value, format) {
    if (value && format) {
        return moment(String(value)).format(format)
    }
}

export function deepClone(object) {
    if (object != null) {
        return JSON.parse(JSON.stringify(object))
    }
}

export function isEmpty(object) {
    return (object == null || object === "" || object.length === 0 || typeof object === 'undefined')
}
